import { useCallback, useMemo } from 'react';
import {
  BillingMethod,
  Capabilities,
  Feature,
  Offer,
  QuoteInformation,
  useOffers,
} from 'queries';
import {
  getOfferInfos,
  useInvoiceFeeOffer,
  useOfferInfosFor,
} from 'pages/StudySetup/GeneralSetup/filterOffers';

export const selectQuoteOffers = (
  values: Partial<QuoteInformation>,
  offers?: Offer[],
  capabilities?: Capabilities,
  invoiceFeeOffer?: Offer,
): Offer[] => {
  const { availableOffers, optionOffers } = getOfferInfos(
    values,
    offers,
    capabilities,
  );

  const shouldAddInvoiceFees =
    !!invoiceFeeOffer &&
    !capabilities?.[Feature.SEND_INVOICE] &&
    values.billingMethod === BillingMethod.SEND_INVOICE;

  return [values.targetingOfferId, values.supportOfferId]
    .map((id) => availableOffers.find((offer) => offer.id === id))
    .concat(
      values.optionsOfferIds?.map((id) =>
        optionOffers.find((offer) => offer.id === id),
      ),
    )
    .concat(shouldAddInvoiceFees ? [invoiceFeeOffer] : [])
    .filter(Boolean) as Offer[];
};

/** The catalog the quote setup form lays out, plus what is selected in it so far. */
export const useQuoteOffers = (
  values: Partial<QuoteInformation>,
  capabilities?: Capabilities,
) => {
  const offerInfos = useOfferInfosFor(values, capabilities);
  const { data: offers } = useOffers();
  const invoiceFeeOffer = useInvoiceFeeOffer();

  const selectedOffers = useMemo(
    () => selectQuoteOffers(values, offers, capabilities, invoiceFeeOffer),
    [values, offers, capabilities, invoiceFeeOffer],
  );

  return { ...offerInfos, selectedOffers };
};

export const useQuoteOfferIds = (capabilities?: Capabilities) => {
  const { data: offers } = useOffers();
  const invoiceFeeOffer = useInvoiceFeeOffer();

  return useCallback(
    (values: Partial<QuoteInformation>) =>
      selectQuoteOffers(values, offers, capabilities, invoiceFeeOffer).map(
        ({ id }) => id,
      ),
    [offers, capabilities, invoiceFeeOffer],
  );
};
